import {
  Button,
  CircularProgress,
  FormControl,
  GridLegacy as Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ModalIOF } from '@old/ui/modal/modal';

import { Plans, taxStatus, coins, validityInsurance, paymentMethods } from '../selects-insurance-data';
import { useState } from 'react';
import { confirmLaSegundaService } from '@old/services/integration/insurance/la-segunda/la-segunda-confirmation';
import { useGlobalStore } from '@old/global-store/use-store';
import { isValidEmail } from '@old/utils/helper';

import { useService } from '@old/hooks/use-service';

type BodyInsuranceForm = Parameters<typeof confirmLaSegundaService>[0]['body'];

type GenericInsuranceModalProps = {
  type: 'lasegunda' | 'galicia' | 'fedpatronal';
  setOpen: (open: boolean) => void;
  setIsSend: (isSend: boolean) => void;
  setIsQuoting: (isQuoting: boolean) => void;
  silobagIds?: string[];
  vigency: number;
  totalSumAssured: number;
  tons: number;
  typeCoin: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  insurance: any;
};

export function ArgInsuranceBodyModal(props: GenericInsuranceModalProps) {
  const { insurance, silobagIds, tons, vigency, totalSumAssured, typeCoin } = props;
  const { setOpen, setIsSend, setIsQuoting } = props;

  const { organizationName, userFullname, email } = useGlobalStore();

  const { trigger: argQuoteEmailTrigger, isLoading } = useService(confirmLaSegundaService, {
    onSuccess: () => {
      setIsSend(true);
      setTimeout(() => {
        setInsuranceForm({
          plan: Plans[0].description,
          duration: validityInsurance[props.type][Math.floor(vigency / 3)].description,
          payment_method: paymentMethods[0].description,
          fiscal_condition: taxStatus[0].description,
          currency: coins[0].description,
          sum_assured: totalSumAssured,
          zip_code: '',
          organization_name: organizationName ? organizationName : 'Sin Organización',
          fullname: '',
          email: '',
          phone: '',
          silobags: silobagIds ?? [],
          observations: '',
          total_weight: tons * 1_000,
        });
      }, 2000);
    },
  });

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [errorTracking, setErrorTracking] = useState<BodyInsuranceForm>({} as BodyInsuranceForm);

  const [insuranceForm, setInsuranceForm] = useState<BodyInsuranceForm>({
    plan: Plans[0].description,
    duration:
      validityInsurance[props.type].find((ins) => ins.id === Math.floor(vigency / 3))?.description ?? '',
    payment_method: paymentMethods[0].description,
    fiscal_condition: taxStatus[0].description,
    currency: coins.find((coin) => coin.id === typeCoin)?.description || coins[0].description,
    sum_assured: totalSumAssured,
    zip_code: '',
    organization_name: organizationName ? organizationName : 'Sin Organización',
    fullname: userFullname ? userFullname : '',
    email: email ? email : '',
    phone: '',
    silobags: silobagIds ?? [],
    polycyHolderTaxId: '',
    insuredTaxId: '',
    beneficiaryTaxId: '',
    observations: '',
    total_weight: tons * 1_000,
  });

  const handleSubmitInsuranceARG = (
    event: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    setFormSubmitted(true);
    const { polycyHolderTaxId, insuredTaxId, beneficiaryTaxId, observations, ...restRequiredFields } =
      insuranceForm;
    if (Object.values(restRequiredFields).some((value) => value === '' || value === 0))
      return { polycyHolderTaxId, insuredTaxId, beneficiaryTaxId, observations }; // Devuelvo los campos opcionales para no marcar error en ellos

    argQuoteEmailTrigger({ body: insuranceForm }, { type: props.type });
  };

  return (
    <ModalIOF.Body>
      <Typography variant="h5" sx={{ fontWeight: 600, margin: '15px' }}>
        Completa el formulario para obtener tu cotización
      </Typography>
      <Stack direction={'row'} spacing={2} sx={{ marginBottom: 3 }}>
        <Typography variant="body2" sx={{ color: '#9d9d9d' }}>
          Silobolsas Seleccionados{' '}
          <span style={{ fontWeight: 600, color: 'black' }}>{silobagIds?.length}</span>
        </Typography>
        <Typography variant="body2" sx={{ color: '#9d9d9d' }}>
          Toneladas cotizadas: <span style={{ fontWeight: 600, color: 'black' }}>{tons}</span>
        </Typography>
      </Stack>
      <Grid container spacing={2} sx={{ textAlign: 'center' }}>
        <Grid item xs={12} sm={4}>
          <FormControl variant="outlined" size="small" style={{ width: '100%' }}>
            <InputLabel>Plan *</InputLabel>
            <Select
              style={{ width: '100%' }}
              onChange={(e) => {
                setInsuranceForm({ ...insuranceForm, plan: e.target.value as string });
              }}
              required
              input={<OutlinedInput label="Plan" />}
              error={formSubmitted && !insuranceForm.plan}
              value={insuranceForm.plan}
            >
              {Plans?.map((item) => (
                <MenuItem key={item.id} value={item.description}>
                  {item.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl variant="outlined" size="small" style={{ width: '100%' }}>
            <InputLabel>Vigencia *</InputLabel>
            <Select
              style={{ width: '100%' }}
              onChange={(e) => {
                setInsuranceForm({ ...insuranceForm, duration: e.target.value as string });
              }}
              required
              input={<OutlinedInput label="Vigencia *" />}
              error={formSubmitted && !insuranceForm.duration}
              value={insuranceForm.duration}
            >
              {validityInsurance[props.type]?.map((item) => (
                <MenuItem key={item.id} value={item.description}>
                  {item.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl variant="outlined" size="small" style={{ width: '100%' }}>
            <InputLabel>Forma de Pago *</InputLabel>
            <Select
              style={{ width: '100%' }}
              required
              onChange={(e) => {
                setInsuranceForm({ ...insuranceForm, payment_method: e.target.value as string });
              }}
              input={<OutlinedInput label="Forma de Pago *" />}
              error={formSubmitted && !insuranceForm.payment_method}
              value={insuranceForm.payment_method}
            >
              {paymentMethods?.map((item) => (
                <MenuItem key={item.id} value={item.description}>
                  {item.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl variant="outlined" size="small" style={{ width: '100%' }}>
            <InputLabel>Condición Fiscal *</InputLabel>
            <Select
              style={{ width: '100%' }}
              required
              onChange={(e) => {
                setInsuranceForm({ ...insuranceForm, fiscal_condition: e.target.value as string });
              }}
              input={<OutlinedInput label="Condición Fiscal *" />}
              error={formSubmitted && !insuranceForm.fiscal_condition}
              value={insuranceForm.fiscal_condition}
            >
              {taxStatus?.map((item) => (
                <MenuItem key={item.id} value={item.description}>
                  {item.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl variant="outlined" size="small" style={{ width: '100%' }}>
            <InputLabel>Moneda *</InputLabel>
            <Select
              style={{ width: '100%' }}
              onChange={(e) => {
                setInsuranceForm({ ...insuranceForm, currency: e.target.value as string });
              }}
              required
              input={<OutlinedInput label="Moneda *" />}
              error={formSubmitted && !insuranceForm.currency}
              value={insuranceForm.currency}
            >
              {coins?.map((item) => (
                <MenuItem key={item.id} value={item.description}>
                  {item.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl variant="outlined" size="small" style={{ width: '100%' }}>
            <Tooltip
              title={
                insuranceForm.sum_assured != 0
                  ? ''
                  : 'La Suma Asegurada es el valor de los granos almacenados en el silobolsa. Se calcula como la cantidad toneladas multiplicadas por el precio del grano.'
              }
            >
              <TextField
                style={{ width: '100%' }}
                size="small"
                required
                label="Suma Asegurada"
                type="number"
                value={insuranceForm.sum_assured}
                onChange={(e) => {
                  setInsuranceForm({
                    ...insuranceForm,
                    sum_assured: e.target.value ? parseInt(e.target.value) : 0,
                  });
                }}
                error={formSubmitted && !insuranceForm.sum_assured}
              />
            </Tooltip>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl variant="outlined" size="small" style={{ width: '100%' }}>
            <TextField
              style={{ width: '100%' }}
              size="small"
              required
              label="Codigo Postal del riesgo a asegurar"
              onChange={(e) => {
                setInsuranceForm({ ...insuranceForm, zip_code: e.target.value });
              }}
              error={formSubmitted && !insuranceForm.zip_code}
            />
          </FormControl>
        </Grid>
      </Grid>
      <Typography sx={{ margin: '10px', fontWeight: 600 }}>Los CUIT relacionados</Typography>
      <Stack direction="row" spacing={2} sx={{ marginBottom: 3 }}>
        <TextField
          size="small"
          sx={{ width: '100%' }}
          label="CUIT Tomador"
          value={insuranceForm.polycyHolderTaxId}
          onChange={(e) => {
            const value = e.target.value;
            if (/^\d*$/.test(value)) {
              setErrorTracking((prev) => ({ ...prev, polycyHolderTaxId: '' }));
              setInsuranceForm({ ...insuranceForm, polycyHolderTaxId: value });
              return;
            }
            setErrorTracking((prev) => ({ ...prev, polycyHolderTaxId: 'Solo se admiten números.' }));
          }}
          onBlur={() => setErrorTracking((prev) => ({ ...prev, polycyHolderTaxId: '' }))}
          error={Boolean(errorTracking.polycyHolderTaxId)}
          helperText={errorTracking.polycyHolderTaxId}
        />
        <TextField
          size="small"
          sx={{ width: '100%' }}
          label="CUIT Asegurado"
          value={insuranceForm.insuredTaxId}
          onChange={(e) => {
            const value = e.target.value;
            if (/^\d*$/.test(value)) {
              setErrorTracking((prev) => ({ ...prev, insuredTaxId: '' }));
              setInsuranceForm({ ...insuranceForm, insuredTaxId: value });
              return;
            }
            setErrorTracking((prev) => ({ ...prev, insuredTaxId: 'Solo se admiten números.' }));
          }}
          onBlur={() => setErrorTracking((prev) => ({ ...prev, insuredTaxId: '' }))}
          error={Boolean(errorTracking.insuredTaxId)}
          helperText={errorTracking.insuredTaxId}
        />
        <TextField
          size="small"
          sx={{ width: '100%' }}
          label="CUIT Beneficiario"
          value={insuranceForm.beneficiaryTaxId}
          onChange={(e) => {
            const value = e.target.value;
            if (/^\d*$/.test(value)) {
              setErrorTracking((prev) => ({ ...prev, beneficiaryTaxId: '' }));
              setInsuranceForm({ ...insuranceForm, beneficiaryTaxId: value });
              return;
            }
            setErrorTracking((prev) => ({ ...prev, beneficiaryTaxId: 'Solo se admiten números.' }));
          }}
          onBlur={() => setErrorTracking((prev) => ({ ...prev, beneficiaryTaxId: '' }))}
          error={Boolean(errorTracking.beneficiaryTaxId)}
          helperText={errorTracking.beneficiaryTaxId}
        />
      </Stack>
      <Typography sx={{ margin: '10px', fontWeight: 600 }}>Tus datos de contacto</Typography>
      <TextField
        size="small"
        sx={{ width: '100%' }}
        required
        label="Organización"
        value={organizationName}
        disabled
        // error={formSubmitted && !insuranceForm.organization_name}
      />
      <Grid container sx={{ marginTop: '15px' }} spacing={2}>
        <Grid item xs={12} sm={4}>
          <FormControl variant="outlined" size="small" style={{ width: '100%' }}>
            <TextField
              style={{ width: '100%' }}
              size="small"
              required
              label="Nombre y apellido"
              value={insuranceForm.fullname}
              onChange={(e) => {
                setInsuranceForm({ ...insuranceForm, fullname: e.target.value });
              }}
              error={formSubmitted && !insuranceForm.fullname}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={4}>
          <FormControl variant="outlined" size="small" style={{ width: '100%' }}>
            <TextField
              style={{ width: '100%' }}
              size="small"
              required
              label="Email"
              value={insuranceForm.email}
              onChange={(e) => {
                setInsuranceForm({ ...insuranceForm, email: e.target.value });
              }}
              error={
                (formSubmitted && !insuranceForm.email) ||
                (insuranceForm.email ? !isValidEmail(insuranceForm.email) : false)
              }
              type="email"
              helperText={
                isValidEmail(insuranceForm.email) || insuranceForm.email == '' ? '' : 'Email inválido'
              }
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={4}>
          <FormControl variant="outlined" size="small" style={{ width: '100%' }}>
            <TextField
              style={{ width: '100%' }}
              size="small"
              required
              label="Celular"
              onChange={(e) => {
                setInsuranceForm({ ...insuranceForm, phone: e.target.value });
              }}
              error={formSubmitted && !insuranceForm.phone}
            />
            <p style={{ fontSize: '12px', margin: '0', textAlign: 'left', color: '#A7A6A6' }}>
              Ejemplo: +5491157059192
            </p>
          </FormControl>
        </Grid>
      </Grid>
      <TextField
        size="small"
        sx={{ marginTop: 2, width: '100%' }}
        label="Observaciones"
        placeholder="..."
        multiline
        rows={4}
        maxRows={4}
        value={insuranceForm.observations}
        onChange={(e) => {
          setInsuranceForm({ ...insuranceForm, observations: e.target.value });
        }}
      />
      <Stack direction="row" spacing={2} justifyContent={'right'} sx={{ marginTop: '15px' }}>
        <Button
          color="error"
          onClick={() => {
            setIsQuoting(false);
            setOpen(false);
          }}
          sx={{ color: 'black' }}
        >
          Cerrar
        </Button>
        <Button
          variant="contained"
          sx={{
            color: '#FFF',
            fontSize: 14,
            borderRadius: 100,
            textTransform: 'none',
            padding: '0px 30px',
            margin: '10px',
            ...insurance.callToActionStyles,
          }}
          onClick={handleSubmitInsuranceARG}
        >
          {isLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Enviar'}
        </Button>
      </Stack>
    </ModalIOF.Body>
  );
}
